import { ReactElement, ReactNode } from 'react'

import { PortableTextBlock, toPlainText } from 'next-sanity'

/**
 * Check if value is a PortableTextBlock or array of PortableTextBlocks
 */
function isPortableTextBlock(value: unknown): value is PortableTextBlock | PortableTextBlock[] {
  if (Array.isArray(value)) {
    return (
      value.length > 0 && typeof value[0] === 'object' && value[0] !== null && '_type' in value[0]
    )
  }
  return typeof value === 'object' && value !== null && '_type' in value
}

/**
 * Recursively extract text from React nodes, including nested elements
 */
function extractTextFromReactNode(node: ReactNode): string {
  if (typeof node === 'string') {
    return node
  }
  if (typeof node === 'number') {
    return String(node)
  }
  if (Array.isArray(node)) {
    return node.map(extractTextFromReactNode).join('')
  }
  if (node && typeof node === 'object' && node !== null && 'props' in node) {
    const element = node as ReactElement
    if (element.props && typeof element.props === 'object' && 'children' in element.props) {
      return extractTextFromReactNode(element.props.children as ReactNode)
    }
  }
  return ''
}

/**
 * Convert a string to a URL-friendly slug
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
}

/**
 * Generate a clean, URL-friendly ID from text element
 * Accepts ReactNode, PortableTextBlock, or arrays of PortableTextBlocks
 */
export function generateElementId(children: ReactNode | PortableTextBlock[]): string {
  let textContent: string

  // Check if it's a PortableTextBlock and use toPlainText
  if (isPortableTextBlock(children)) {
    textContent = toPlainText(children as any).trim()
  } else {
    // Extract text content from children, handling nested React elements
    textContent = extractTextFromReactNode(children as ReactNode).trim()
  }

  // Create a URL-friendly slug
  const slug = slugify(textContent)

  return slug || 'heading'
}
